import React, { useEffect, useState } from "react";
import Sidebar from "../sidebar/Sidebar";
import { Form, Field, Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Header from "../../Navbar/Header";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { refreshExamResultStatus, userExamDetailsGetById, userExamDetailsUpdate } from "./JotSlice";
import * as Yup from "yup";
import LoadingSpinner from "../../../component/resuable/LoadingSpinner";
import { getPartnerStatus } from "./partnerStatus";
import JotSyncPanel from "./JotSyncPanel";

// Deadline phases returned by the backend (Partner Exam Registration Data Operation Policy, section 5).
const DEADLINE_PHASE_LABELS = {
  open: "Open for edits",
  corrections_only: "Corrections only (after D-10)",
  frozen: "Data frozen (D-5)",
  emergency_only: "Emergency exceptions only (D-3)",
  past: "Exam completed",
};

export default function EditUserInfo() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id, applied_date } = useParams();
  const [searchParams] = useSearchParams();
  const application_id = searchParams.get("application_id");

  const JOT_DATA = useSelector((state) => state.jot);
  const { getUserExamDetailsById, refreshingResultStatus } = useSelector((state) => state.jot);
  const getUserExamDetails = getUserExamDetailsById?.data;

  const [previewCertificateImage, setPreviewCertificateImage] = useState("");

  const [initialState, setInitialState] = useState({
    first_name: "",
    last_name: "",
    romaji_name: "",
    email: "",
    phone: "",
    gender: "",
    address: "",
    country: "",
    state: "",
    city: "",
    postal_code: "",
    applied_at: "",
    dob: "",
    exam_level: "",
    id_photo: null,
    person_photo: null,
    certificate: null,
    result: "",
    updated_at: "",
    user_id: "",
    exam_date: null,
    exam_time: "",
    partner_status: "",
    partner_candidate_id: "",
    partner_exam_registration_id: "",
    partner_received_at: "",
    test_venue_code: "",
    test_venue_name: "",
    idempotency_key: "",
  });

  // D-10 / D-5 / D-3 milestones (Japan business days, venue timezone) from the backend.
  const deadlines = getUserExamDetails?.deadlines;
  const toLocalDate = (isoDate) => new Date(`${isoDate}T00:00:00`);
  const formatDeadline = (isoDate) => (isoDate ? format(toLocalDate(isoDate), "dd/MM/yyyy") : "");

  // The registration must reach JOT before D-10, so the earliest exam date is
  // the first one whose D-10 deadline is still ahead. The backend enforces it too.
  const minExamDate = (() => {
    const date = deadlines?.earliest_exam_date ? toLocalDate(deadlines.earliest_exam_date) : new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  })();

  // Once registered with JOT the slot is locked, so the minimum must not block
  // later result / certificate updates on a date that is now close or past.
  const examDateLocked = Boolean(getUserExamDetails?.partner_candidate_id);

  const validationSchema = Yup.object().shape({
    exam_date: examDateLocked
      ? Yup.date().required("Exam date required")
      : Yup.date()
          .required("Exam date required")
          .min(minExamDate, `Exam date must be on or after ${format(minExamDate, "dd/MM/yyyy")} (10 Japan business days ahead)`),
    exam_time: Yup.string().required("Exam time required"),
    certificate: Yup.string().required("Certificate required"),
  });

  useEffect(() => {
    const params = {
      id: id,
      applied_date: applied_date,
      application_id: application_id,
    };
    dispatch(userExamDetailsGetById(params));
  }, [dispatch, id]);

  useEffect(() => {
    if (getUserExamDetailsById?.data) {
      const UpdatedDetails = {
        ...getUserExamDetails,
        id_photo: getUserExamDetails?.id_photo || null,
        person_photo: getUserExamDetails?.person_photo || null,
        certificate: getUserExamDetails?.certificate || null,
        exam_date: getUserExamDetails?.exam_date || null,
        // Keep the time input controlled, else a record without a time keeps
        // showing the previously opened record's value.
        exam_time: getUserExamDetails?.exam_time || "",
        romaji_name: getUserExamDetails?.romaji_name || "",
      };
      setInitialState(UpdatedDetails);
    }
  }, [getUserExamDetailsById]);

  // The route :id is the student's user_id (the GET looks up by Exam.user_id).
  // The exam endpoints key off Exam.id, which the GET returns as application_id.
  const applicationId = getUserExamDetails?.application_id;

  const UpdateUserExamDetails = (values) => {
    const formData = new FormData();

    formData.append("result", values.result || "");

    if (values.exam_date) {
      const selectedDate = new Date(values.exam_date);
      selectedDate.setMinutes(selectedDate.getMinutes() - selectedDate.getTimezoneOffset());
      formData.append("exam_date", selectedDate.toISOString().split("T")[0]);
    }
    if (values.exam_time) {
      formData.append("exam_time", values.exam_time);
    }
    if (values.certificate instanceof File) {
      formData.append("certificate", values.certificate);
    } else {
      formData.append("certificate", values.certificate || "");
    }

    if (!applicationId) {
      toast.error("Application details not loaded yet. Please retry.");
      return;
    }

    const param = { id: applicationId, data: formData };
    dispatch(userExamDetailsUpdate(param)).then((res) => {
      if (res?.payload?.status === 200 && res?.payload?.data?.jot_status?.error) {
        // Saved locally but JOT rejected or never received it — stay on the page
        // so the admin can correct the data and save again.
        toast.error(res?.payload?.data?.message);
        dispatch(userExamDetailsGetById({ id: id, applied_date: applied_date, application_id: application_id }));
      } else if (res?.payload?.status === 200) {
        toast.success(res?.payload?.data?.message);
        navigate(-1);
      } else {
        // Deadline / 30-day-gap rejections come back as 400 with data.message.
        toast.error(res?.payload?.data?.message || res?.payload?.data?.error || "Unable to update the exam details.");
      }
    });
  };

  // Re-checks the registration with JOT and pulls the latest status onto the record
  const refreshResultStatus = () => {
    if (!applicationId) {
      toast.error("Application details not loaded yet. Please retry.");
      return;
    }

    dispatch(refreshExamResultStatus(applicationId)).then((res) => {
      if (refreshExamResultStatus.fulfilled.match(res)) {
        if (res?.payload?.success) {
          toast.success(res?.payload?.message || "Registration status refreshed successfully");
          dispatch(userExamDetailsGetById({ id: id, applied_date: applied_date, application_id: application_id }));
        } else {
          toast.info(res?.payload?.message || "Registration status has not changed yet");
        }
      } else {
        toast.error(res?.payload || "Unable to refresh registration status.");
      }
    });
  };

  const cancelsubmit = () => {
    navigate(-1);
  };

  const downloadFile = (imageURL) => {
    const newTab = window.open(imageURL, "_blank");
    if (!newTab) {
      const link = document.createElement("a");
      link.href = imageURL;
      link.download = imageURL;
      link.click();
    }
  };

  return (
    <>
      {JOT_DATA?.loading && <LoadingSpinner />}
      <div className="container-fluid">
        <div className="row">
          <Header />
          <div className="col-2 p-0">
            <Sidebar />
          </div>
          <div className="col-10 mt-2 px-3">
            <div className="card custom-card">
              <div className="row mb-3 mt-2">
                <Formik
                  initialValues={initialState}
                  enableReinitialize
                  onSubmit={UpdateUserExamDetails}
                  validationSchema={validationSchema}
                >
                  {({ values, setFieldValue, errors, touched }) => (
                    <Form>
                      <div className="row px-4 mt-0 mt-sm-2">
                        <div className="col-sm-6 mb-2 mb-sm-0">
                          <label className="form-label">First Name</label>
                          <Field placeholder="Enter Name" className="form-control" name="first_name" value={values?.first_name} disabled />
                        </div>
                        <div className="col-sm-6 mb-2 mb-sm-0">
                          <label className="form-label">Last Name</label>
                          <Field
                            placeholder="Enter Last Name"
                            className="form-control"
                            name="last_name"
                            value={values?.last_name}
                            disabled
                          />
                        </div>
                      </div>
                      <div className="row px-4 mt-0 mt-sm-2">
                        <div className="col-sm-6 mb-2 mb-sm-0">
                          <label className="form-label">Romaji Name (Latin letters)</label>
                          <Field className="form-control" name="romaji_name" value={values?.romaji_name} disabled />
                          {getUserExamDetails?.romaji_name_error && (
                            <small className="text-danger">{getUserExamDetails.romaji_name_error}</small>
                          )}
                        </div>
                      </div>
                      <div className="row px-4 mt-0 mt-sm-2">
                        <div className="col-sm-6 mb-2 mb-sm-0">
                          <label className="form-label">Date of Birth</label>
                          <DatePicker
                            placeholderText="Select the DOB"
                            className="form-control"
                            name="dob"
                            peekNextMonth
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            value={values?.dob ? format(new Date(values.dob), "dd/MM/yyyy") : ""}
                            onChange={(date) => {
                              setFieldValue("dob", date);
                            }}
                            disabled
                          />
                        </div>
                        <div className="col-sm-6 mb-2 mb-sm-0">
                          <label className="form-label">Email</label>
                          <Field placeholder="Enter Email" className="form-control" name="email" value={values?.email} disabled />
                        </div>
                      </div>
                      <div className="row px-4 mt-0 mt-sm-2">
                        <div className="col-sm-6 mb-2 mb-sm-0">
                          <label className="form-label">Phone Number</label>
                          <Field placeholder="Enter Passport Number" className="form-control" name="phone" value={values?.phone} disabled />
                        </div>
                        <div className="col-sm-6 mb-2 mb-sm-0">
                          <label className="form-label">Gender</label>
                          <Field placeholder="Enter gender" className="form-control" name="gender" value={values?.gender} disabled />
                        </div>
                      </div>
                      <div className="row px-4 mt-0 mt-sm-2">
                        <div className="col-sm-6 mb-2 mb-sm-0">
                          <label className="form-label">Address</label>
                          <Field placeholder="Enter address" className="form-control" name="address" value={values?.address} disabled />
                        </div>
                        <div className="col-sm-6 mb-2 mb-sm-0">
                          <label className="form-label">Country</label>
                          <Field placeholder="Enter country" className="form-control" name="country" value={values?.country} disabled />
                        </div>
                      </div>
                      <div className="row px-4 mt-0 mt-sm-2">
                        <div className="col-sm-6 mb-2 mb-sm-0">
                          <label className="form-label">State</label>
                          <Field placeholder="Enter state" className="form-control" name="state" value={values?.state} disabled />
                        </div>
                        <div className="col-sm-6 mb-2 mb-sm-0">
                          <label className="form-label">City</label>
                          <Field placeholder="Enter city" className="form-control" name="city" value={values?.city} disabled />
                        </div>
                      </div>
                      <div className="row px-4 mt-0 mt-sm-2">
                        <div className="col-sm-6 mb-2 mb-sm-0">
                          <label className="form-label">Post Code</label>
                          <Field
                            placeholder="Enter post code"
                            className="form-control"
                            name="postal_code"
                            value={values?.postal_code}
                            disabled
                          />
                        </div>
                        <div className="col-sm-6 mb-2 mb-sm-0">
                          <label className="form-label">Applied Date</label>
                          <Field
                            placeholder="Enter applied date"
                            className="form-control"
                            name="applied_at"
                            value={values?.applied_at}
                            disabled
                          />
                        </div>
                      </div>
                      <div className="row px-4 mt-0 mt-sm-2">
                        <div className="col-sm-6 mb-2 mb-sm-0">
                          <label className="form-label">Exam Level</label>
                          <Field
                            placeholder="Enter exam level"
                            className="form-control"
                            name="exam_level"
                            value={values?.exam_level}
                            disabled
                          />
                        </div>
                        <div className="col-sm-6 mb-2 mb-sm-0">
                          <label className="form-label">ID Photo</label>
                          <div
                            className="d-flex align-items-center justify-content-center border rounded p-1 bg-light cursor-pointer"
                            onClick={() => downloadFile(values?.id_photo)}
                          >
                            <i class="fas fa-cloud-download-alt fs-3 me-3"></i>
                            {values?.id_photo ? "View ID" : "ID Unavailable"}
                          </div>
                        </div>
                      </div>
                      <div className="row px-4 mt-0 mt-sm-2">
                        <div className="col-sm-6 mb-2 mb-sm-0">
                          <label className="form-label">Photo</label>
                          <div
                            className="d-flex align-items-center justify-content-center border rounded p-1 bg-light cursor-pointer"
                            onClick={() => downloadFile(values?.person_photo)}
                          >
                            <i class="fas fa-cloud-download-alt fs-3 me-3"></i>
                            {values?.person_photo ? "View Photo" : "Photo Unavailable"}
                          </div>
                        </div>
                        <div className="col-sm-6 mb-2 mb-sm-0">
                          <label className="form-label">Exam Date</label>
                          <DatePicker
                            placeholderText="Select the date"
                            className="form-control"
                            name="exam_date"
                            peekNextMonth
                            disabled={values?.partner_candidate_id ? true : false}
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            value={values?.exam_date ? format(new Date(values.exam_date), "dd/MM/yyyy") : ""}
                            onChange={(date) => {
                              setFieldValue("exam_date", date);
                            }}
                            minDate={minExamDate}
                          />
                          {errors?.exam_date && touched.exam_date && <p className="error">{errors?.exam_date}</p>}
                          {deadlines?.error ? (
                            <small className="text-danger d-block">{deadlines.error}</small>
                          ) : deadlines?.d10 ? (
                            <small className="text-muted d-block">
                              Edit deadline (D-10): {formatDeadline(deadlines.d10)} · Freeze (D-5): {formatDeadline(deadlines.d5)} ·{" "}
                              {DEADLINE_PHASE_LABELS[deadlines.phase] || deadlines.phase}
                            </small>
                          ) : deadlines?.earliest_exam_date ? (
                            <small className="text-muted d-block">
                              Earliest exam date: {formatDeadline(deadlines.earliest_exam_date)} (10 Japan business days ahead)
                            </small>
                          ) : null}
                        </div>
                      </div>
                      <div className="row px-4 mt-0 mt-sm-2">
                        <div className="col-sm-6 mb-2 mb-sm-0">
                          <label className="form-label">Exam Time</label>
                          <Field
                            type="time"
                            className="form-control"
                            name="exam_time"
                            disabled={values?.partner_candidate_id ? true : false}
                            value={values?.exam_time || ""}
                            onChange={(e) => {
                              setFieldValue("exam_time", e.target.value);
                            }}
                          />
                          {errors?.exam_time && touched.exam_time && <p className="error">{errors?.exam_time}</p>}
                        </div>
                        <div className="col-sm-6 mb-2 mb-sm-0"></div>
                      </div>
                      <div className="row px-4 mt-0 mt-sm-2">
                        <div className="col-sm-6 mb-2 mb-sm-0">
                          <label className="form-label">Payment Status</label>
                          <Field
                            placeholder="Enter payment status"
                            className="form-control"
                            name="payment_status"
                            value={values?.payment_status}
                            disabled
                          />
                        </div>
                        <div className="col-sm-6 mb-2 mb-sm-0">
                          <label className="form-label">Payment Amount</label>
                          <Field
                            placeholder="Enter payment amount"
                            className="form-control"
                            name="payment_amount"
                            value={values?.payment_amount}
                            disabled
                          />
                        </div>
                      </div>
                      <div className="row px-4 mt-0 mt-sm-2">
                        <div className="col-sm-6 mb-2 mb-sm-0">
                          <label className="form-label">Razorpay Order ID</label>
                          <Field
                            placeholder="Enter razorpay order id"
                            className="form-control"
                            name="razorpay_order_id"
                            value={values?.razorpay_order_id}
                            disabled
                          />
                        </div>
                        <div className="col-sm-6 mb-2 mb-sm-0">
                          <label className="form-label">Razorpay Payment ID</label>
                          <Field
                            placeholder="Enter razorpay payment id"
                            className="form-control"
                            name="razorpay_payment_id"
                            value={values?.razorpay_payment_id}
                            disabled
                          />
                        </div>
                      </div>
                      {/* JOT partner registration — refreshed from the partner API, read only here */}
                      <div className="row px-4 mt-3">
                        <div className="col-12 d-flex align-items-center justify-content-between flex-wrap gap-2 border-bottom pb-2 mb-2">
                          <h6 className="mb-0">JOT Registration Details</h6>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            type="button"
                            title="Re-check the registration status with JOT"
                            onClick={refreshResultStatus}
                            disabled={refreshingResultStatus}
                          >
                            {refreshingResultStatus ? (
                              <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                            ) : (
                              <i className="fas fa-sync-alt me-1"></i>
                            )}
                            Refresh Registration Status
                          </button>
                        </div>
                      </div>
                      <div className="row px-4 mt-0 mt-sm-2">
                        <div className="col-sm-6 mb-2 mb-sm-0">
                          <label className="form-label">Registration Status</label>
                          <div className="form-control d-flex align-items-center bg-light">
                            <span className={`badge bg-${getPartnerStatus(values?.partner_status).tone} text-nowrap`}>
                              {getPartnerStatus(values?.partner_status).label}
                            </span>
                          </div>
                        </div>
                        <div className="col-sm-6 mb-2 mb-sm-0">
                          <label className="form-label">Received At</label>
                          <Field
                            placeholder="Received at"
                            className="form-control"
                            name="partner_received_at"
                            value={values?.partner_received_at || ""}
                            disabled
                          />
                        </div>
                      </div>
                      <div className="row px-4 mt-0 mt-sm-2">
                        <div className="col-sm-6 mb-2 mb-sm-0">
                          <label className="form-label">Candidate ID</label>
                          <Field
                            placeholder="Candidate id"
                            className="form-control"
                            name="partner_candidate_id"
                            value={values?.partner_candidate_id || ""}
                            disabled
                          />
                        </div>
                        <div className="col-sm-6 mb-2 mb-sm-0">
                          <label className="form-label">Exam Registration ID</label>
                          <Field
                            placeholder="Exam registration id"
                            className="form-control"
                            name="partner_exam_registration_id"
                            value={values?.partner_exam_registration_id || ""}
                            disabled
                          />
                        </div>
                      </div>
                      <div className="row px-4 mt-0 mt-sm-2">
                        <div className="col-sm-6 mb-2 mb-sm-0">
                          <label className="form-label">Test Venue Code</label>
                          <Field
                            placeholder="Test venue code"
                            className="form-control"
                            name="test_venue_code"
                            value={values?.test_venue_code || ""}
                            disabled
                          />
                        </div>
                        <div className="col-sm-6 mb-2 mb-sm-0">
                          <label className="form-label">Test Venue Name</label>
                          <Field
                            placeholder="Test venue name"
                            className="form-control"
                            name="test_venue_name"
                            value={values?.test_venue_name || ""}
                            disabled
                          />
                        </div>
                      </div>
                      <div className="row px-4 mt-0 mt-sm-2 mb-2">
                        <div className="col-12 mb-2 mb-sm-0">
                          <label className="form-label">Idempotency Key</label>
                          <Field
                            placeholder="Idempotency key"
                            className="form-control"
                            name="idempotency_key"
                            value={values?.idempotency_key || ""}
                            disabled
                          />
                        </div>
                      </div>

                      {/* Sync state, corrections and history (partner policy 6, 9) */}
                      <JotSyncPanel
                        details={getUserExamDetails}
                        onChanged={() => dispatch(userExamDetailsGetById({ id: id, applied_date: applied_date, application_id: application_id }))}
                      />

                      <div className="row px-4 mt-0 mt-sm-2">
                        <div className="col-sm-6 mb-2 mb-sm-0">
                          <label className="form-label">Issue Hall ticket</label>
                          <input
                            className="form-control"
                            type="file"
                            name="certificate"
                            aria-label="Upload certificate"
                            onChange={(event) => {
                              const file = event.currentTarget.files[0];
                              if (file instanceof Blob || file instanceof File) {
                                setFieldValue("certificate", file);
                                const reader = new FileReader();
                                reader.onload = (e) => {
                                  const imagePreviewUrl = e.target.result;
                                  setPreviewCertificateImage(imagePreviewUrl);
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                          />
                          <div className="col-sm-6 mb-2 mt-2 mb-sm-0 profileimage">
                            {previewCertificateImage ? (
                              previewCertificateImage.startsWith("data:application/pdf") ? (
                                <embed src={previewCertificateImage} type="application/pdf" width="100%" height="500px" />
                              ) : (
                                <img src={previewCertificateImage} alt="certificate Preview" />
                              )
                            ) : values.certificate && typeof values.certificate === "string" ? (
                              values.certificate.includes(".pdf") ? (
                                <embed src={values.certificate} type="application/pdf" width="100%" height="500px" />
                              ) : (
                                <img src={values.certificate} alt="certificate" />
                              )
                            ) : null}
                          </div>
                          {errors?.certificate && touched.certificate && <p className="error">{errors?.certificate}</p>}
                        </div>
                        <div className="col-sm-6 mb-2 mb-sm-0">
                          <label className="form-label">Result</label>
                          <Field
                            as="select"
                            placeholder="Select result status"
                            className="form-select"
                            name="result"
                            value={values?.result}
                            disabled={values?.exam_date === null || values?.certificate === null ? true : false}
                          >
                            <option value="" hidden>
                              select status
                            </option>
                            <option value="Awaiting Results">Awaiting Results</option>
                            <option value="Passed">Passed</option>
                            <option value="Failed">Failed</option>
                            <option value="Absent">Absent</option>
                          </Field>

                          <p className="secondary">
                            {values?.exam_date === null || values?.certificate === null
                              ? "Note : Exam date and Certificate needed to access result"
                              : null}
                          </p>
                        </div>
                      </div>
                      <div className="row px-4 mt-0 mt-sm-2"></div>
                      <div className="px-4 mt-3">
                        <button className="cancel-button ms-2" type="button" onClick={cancelsubmit}>
                          Cancel
                        </button>
                        <button className="btn defult-button ms-2" type="submit">
                          Submit
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
